import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { Params } from '@angular/router';

@Directive()
export abstract class DsAbstractButtonComponent {
  /**
   * Controls the size of the button. Sizing is described in Figma Design System
   *
   * @see https://www.figma.com/design/XWMrlpfWAaF8xB2CpN8Zk3/%F0%9F%93%98-Strata-Design-System?node-id=13050-12247&p=f&m=dev
   * @default 'md'
   */
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' = 'md';

  /**
   * Controls the visual theme/style of the button. Styling is described in Figma Design System
   *
   * @see https://www.figma.com/design/XWMrlpfWAaF8xB2CpN8Zk3/%F0%9F%93%98-Strata-Design-System?node-id=13050-12247&p=f&m=dev
   * @default 'primary'
   */
  @Input() theme: 'primary' | 'secondary' | 'secondary-gray' | 'tertiary' | 'tertiary-gray' = 'primary';

  /**
   * Name of the icon to display inside the button. (Material Symbols)
   *
   * @see https://fonts.google.com/icons
   */
  @Input() icon = '';

  /**
   * Icon font set to use for icon.
   *
   * @default 'material-symbols-rounded'
   */
  @Input() iconFontSet: 'material-symbols-outlined' | 'material-symbols-rounded' | 'material-symbols-sharp' = 'material-symbols-rounded';

  /**
   * Determines whether the button should use "destructive" styling.
   *
   * @see https://www.figma.com/design/XWMrlpfWAaF8xB2CpN8Zk3/%F0%9F%93%98-Strata-Design-System?node-id=13050-12247&p=f&m=dev
   * @default false
   */
  @Input() isDestructive = false;

  /**
   * Determines whether the button is disabled.
   *
   * @default false
   */
  @Input() isDisabled = false;

  /**
   * Determines whether the button is in a loading state.
   * When true, the button is disabled and a spinner replaces the icon.
   *
   * @default false
   */
  @Input() isLoading = false;

  /**
   * Router link applied to the button.
   * When provided, the button will render as a navigation trigger
   * and perform Angular routing instead of emitting a click action.
   *
   * Accepts the same formats as Angular `routerLink`.
   *
   * @default null
   */
  @Input() routerLink: string | string[] | null = null;

  /**
   * Query parameters to append to the router link.
   * Used only when `routerLink` is provided.
   *
   * @default {}
   */
  @Input() queryParams: Params = {};

  /**
   * Event emitted when the button is clicked.
   */
  @Output() buttonClick: EventEmitter<void> = new EventEmitter<void>();
}
